/**
 * placementClusters — Step 4 of CANVAS_PLACEMENT_LANGUAGE.
 *
 * Past about 80 nodes, the canvas can't hold every leaf at a readable
 * zoom (§3.4). We fold "context fan-outs" — hubs with many degree-1
 * unknown neighbors — into a single synthetic-cluster bubble that the
 * user can click to expand.
 *
 * Detection rule (auto-clustering for hubs that didn't declare a
 * `placement_cluster` themselves):
 *   1. Hub must have ≥ MIN_LEAVES_PER_HUB degree-1 outgoing/incoming
 *      neighbors that:
 *        - have no entity_name (i.e. unenriched)
 *        - have no placement_role of subject / source / sink / relay /
 *          shared_relay (context / undeclared only)
 *        - are not locked (user-pinned)
 *        - are not already synthetic
 *   2. Each such hub gets ONE synthetic-cluster node attached, replacing
 *      the leaves on the canvas. The leaves are stashed under reason
 *      'cluster' so they can be restored on expand.
 *
 * This utility is pure (no React, no global state) — it operates on a
 * Cytoscape instance and the stash store. Caller decides when to run it
 * (e.g. in the layout settle timer when total node count ≥ 80).
 *
 * References:
 *   india-le-platform/docs/CANVAS_PLACEMENT_LANGUAGE.md §3.4, §12.3
 */
import { useStashStore } from '../stores/useStashStore';
import { useGraphStore } from '../stores/useGraphStore';

export const CLUSTER_MIN_LEAVES_PER_HUB = 10;
export const CLUSTER_AUTO_THRESHOLD_NODES = 80;
export const CLUSTER_REASON = 'cluster' as const;
/**
 * How long a hub stays off-limits for auto-clustering after a user
 * expand. Prevents the layout-settle auto-cluster from immediately
 * re-folding leaves the user just clicked to expand.
 */
export const CLUSTER_EXPAND_COOLDOWN_MS = 15_000;

// Module-scope set of hub ids that the user recently expanded. Entries
// age out after CLUSTER_EXPAND_COOLDOWN_MS so a later auto-cluster pass
// can pick them up again if the structure still warrants it.
const _recentlyExpandedHubs = new Map<string, number>();

/** A detected cluster opportunity — hub + its eligible leaves. */
export interface ClusterCandidate {
    hubId: string;
    hubLabel: string;
    leafIds: string[];
}

/** Identify whether a node is an "unknown context leaf" for clustering. */
function isContextLeaf(node: any, cy: any): boolean {
    if (!node || !node.length) return false;
    if (node.locked()) return false;
    if (node.hasClass('synthetic-cluster')) return false;
    if (node.hasClass('manually-positioned')) return false;
    const role = String(node.data('placement_role') || '');
    if (role === 'subject' || role === 'source' || role === 'sink' ||
        role === 'relay' || role === 'shared_relay') return false;
    if (node.data('entity_name')) return false;
    // Must be degree-1: only one connecting edge into the rest of the graph.
    const edges = cy.edges().filter((e: any) =>
        e.data('source') === node.id() || e.data('target') === node.id()
    );
    return edges.length === 1;
}

/**
 * Walk the canvas and identify hubs with ≥ MIN_LEAVES_PER_HUB unknown
 * context leaves. Returns candidates sorted by leaf-count desc (biggest
 * collapse opportunities first).
 */
export function detectContextClusters(cy: any): ClusterCandidate[] {
    if (!cy || cy.nodes().length === 0) return [];

    // Prune cooldown entries that are older than the window so a long-lived
    // session doesn't accumulate stale ids.
    const now = Date.now();
    for (const [id, ts] of _recentlyExpandedHubs.entries()) {
        if (now - ts > CLUSTER_EXPAND_COOLDOWN_MS) _recentlyExpandedHubs.delete(id);
    }

    const candidates: ClusterCandidate[] = [];

    cy.nodes().forEach((hub: any) => {
        if (hub.hasClass('synthetic-cluster')) return;
        // Skip hubs the user just expanded — they'd otherwise re-collapse
        // on the very next layout settle.
        if (_recentlyExpandedHubs.has(hub.id())) return;

        // Find this hub's leaves
        const leaves: any[] = [];
        cy.edges().forEach((edge: any) => {
            const src = edge.data('source');
            const tgt = edge.data('target');
            let leafId: string | null = null;
            if (src === hub.id()) leafId = tgt;
            else if (tgt === hub.id()) leafId = src;
            if (!leafId) return;
            const leaf = cy.getElementById(leafId);
            if (isContextLeaf(leaf, cy)) {
                leaves.push(leaf);
            }
        });

        if (leaves.length >= CLUSTER_MIN_LEAVES_PER_HUB) {
            candidates.push({
                hubId: hub.id(),
                hubLabel: String(hub.data('entity_name') || hub.data('label') || hub.id().slice(0, 10)),
                leafIds: leaves.map(l => l.id()),
            });
        }
    });

    return candidates.sort((a, b) => b.leafIds.length - a.leafIds.length);
}

/**
 * Stash the cluster's leaves and add a synthetic-cluster node connected
 * to the hub. The synthetic-cluster carries the leaf ids in its data so
 * `expandCluster()` can restore them.
 *
 * Idempotent — if the leaves are already stashed under reason 'cluster',
 * this is a no-op.
 */
export function collapseClusters(cy: any, candidates: ClusterCandidate[]): number {
    if (!cy || candidates.length === 0) return 0;
    const graph = useGraphStore.getState();
    const stashStore = useStashStore.getState();

    let totalCollapsed = 0;

    for (const c of candidates) {
        if (c.leafIds.length === 0) continue;

        // Skip if any leaf is already stashed under cluster reason — means
        // we ran on the same hub already (idempotent re-run safety).
        const anyAlreadyStashed = c.leafIds.some(id => stashStore.hasEntry(id));
        if (anyAlreadyStashed) continue;

        // Build the synthetic node data BEFORE we modify elements/stash.
        // R24: declare placement_role; layout owns coordinates — do not write x/y.
        const syntheticId = `cluster:${c.hubId}`;
        const syntheticEl = {
            data: {
                id: syntheticId,
                label: `+${c.leafIds.length} unknown wallets`,
                type: 'synthetic_cluster',
                cluster_leaf_ids: c.leafIds,
                cluster_hub_id: c.hubId,
                placement_role: 'context',
                placement_cluster: c.hubId,
            },
            classes: 'synthetic-cluster',
        };
        const connectorEdge = {
            data: {
                id: `edge:${syntheticId}`,
                source: c.hubId,
                target: syntheticId,
                edgeType: 'cluster-link',
                label: '',
            },
            classes: 'synthetic-link',
        };

        // 1. Stash the leaves (canvasController.stash would do this too but
        //    we want to keep this util free of the controller import — the
        //    controller calls this util, not the other way around).
        const elements = graph.elements;
        const idSet = new Set(c.leafIds);
        const stashEntries: Array<{ id: string; entry: any }> = [];
        const removeIds: string[] = [];

        for (const el of elements) {
            if (!el.data?.id) continue;
            // Stash the leaf node + its single connecting edge.
            if (idSet.has(el.data.id)) {
                stashEntries.push({
                    id: el.data.id,
                    entry: {
                        element: el,
                        group: 'nodes',
                        reason: CLUSTER_REASON,
                        stashedBy: 'cluster',
                        stashedAt: Date.now(),
                        clusterParentId: syntheticId,
                    },
                });
                removeIds.push(el.data.id);
            } else if (el.data?.source && el.data?.target) {
                if (idSet.has(el.data.source) || idSet.has(el.data.target)) {
                    stashEntries.push({
                        id: el.data.id,
                        entry: {
                            element: el,
                            group: 'edges',
                            reason: CLUSTER_REASON,
                            stashedBy: 'cluster',
                            stashedAt: Date.now(),
                            clusterParentId: syntheticId,
                        },
                    });
                    removeIds.push(el.data.id);
                }
            }
        }

        if (stashEntries.length === 0) continue;

        stashStore.setEntries(stashEntries);
        graph.removeElements(removeIds);
        graph.addElements([syntheticEl, connectorEdge]);
        totalCollapsed += c.leafIds.length;
    }

    return totalCollapsed;
}

/**
 * Expand a synthetic-cluster back into its leaves. Inverse of
 * collapseClusters for a single bubble.
 */
export function expandCluster(cy: any, syntheticId: string): number {
    if (!cy) return 0;
    const node = cy.getElementById(syntheticId);
    if (!node || node.length === 0) return 0;

    const leafIds: string[] = node.data('cluster_leaf_ids') || [];
    if (leafIds.length === 0) return 0;

    // Record the hub so the next auto-cluster pass doesn't immediately
    // re-fold these leaves. Entry ages out after CLUSTER_EXPAND_COOLDOWN_MS.
    const hubId: string | undefined = node.data('cluster_hub_id');
    if (hubId) _recentlyExpandedHubs.set(hubId, Date.now());

    const graph = useGraphStore.getState();
    const stashStore = useStashStore.getState();

    // Find every stash entry whose clusterParentId === syntheticId. That
    // catches both leaves and their edges (we stamped both at collapse).
    const allEntries = stashStore.entries;
    const toRestore: any[] = [];
    const toRemoveFromStash: string[] = [];

    for (const [id, entry] of Object.entries(allEntries)) {
        if ((entry as any).clusterParentId === syntheticId) {
            toRestore.push((entry as any).element);
            toRemoveFromStash.push(id);
        }
    }

    if (toRestore.length === 0) return 0;

    // 1. Remove the synthetic bubble + its connector edge.
    graph.removeElements([syntheticId, `edge:${syntheticId}`]);
    // 2. Drop the stash entries.
    stashStore.removeEntries(toRemoveFromStash);
    // 3. Re-add the leaves + their edges.
    graph.addElements(toRestore);

    return leafIds.length;
}

/**
 * High-level entry point — detect + collapse in one call. Returns the
 * total number of leaves folded. Safe to call repeatedly; idempotent.
 */
export function clusterContextLeaves(cy: any): { candidates: ClusterCandidate[]; collapsed: number } {
    const candidates = detectContextClusters(cy);
    const collapsed = collapseClusters(cy, candidates);
    return { candidates, collapsed };
}

// ── Step 5 — Component awareness (CANVAS_PLACEMENT_LANGUAGE §3.6) ────────────
//
// "The subject can be islanded" — discovered during a 227-node ETH
// investigation where the root had zero edges to the rest of the canvas.
// When this happens, the four other primitives (anchor, axis, column,
// cluster) don't help because there's no flow to lay out.

export interface SubjectIslandStatus {
    /** Subject node id (lowercased EVM-style match against candidates). */
    subjectId: string | null;
    /** True when subject is on canvas but has zero on-canvas edges. */
    islanded: boolean;
    /** Total node count, for the "{N} addresses" banner copy. */
    totalNodes: number;
    /** Components other than the subject's, counted for the footer. */
    orphanComponentCount: number;
}

/**
 * Detect whether the subject is islanded on the current canvas.
 *
 * "Islanded" = subject node is present AND has zero connecting edges to
 * any other node on the canvas. This is the §3.6 trigger for the subject
 * banner.
 *
 * `subjectCandidates` is a list of strings to match against node ids
 * (case-insensitive) — typically [effectiveAddress, investigationRootId]
 * from the Workbench page. Order doesn't matter; first match wins.
 */
export function detectSubjectIsland(
    elements: any[],
    subjectCandidates: Array<string | null | undefined>,
): SubjectIslandStatus {
    const candidatesLower = new Set<string>();
    for (const c of subjectCandidates) {
        if (!c) continue;
        candidatesLower.add(String(c));
        candidatesLower.add(String(c).toLowerCase());
    }

    let subjectId: string | null = null;
    const nodeIds = new Set<string>();
    for (const el of elements) {
        if (!el?.data?.id) continue;
        if (el.data.source) continue; // edges
        const id = el.data.id;
        nodeIds.add(id);
        if (!subjectId) {
            if (candidatesLower.has(id) || candidatesLower.has(id.toLowerCase())) {
                subjectId = id;
            } else if (el.data.placement_role === 'subject') {
                subjectId = id;
            }
        }
    }

    if (!subjectId) {
        return { subjectId: null, islanded: false, totalNodes: nodeIds.size, orphanComponentCount: 0 };
    }

    // Count edges touching the subject and build adjacency for component count.
    const adj = new Map<string, Set<string>>();
    for (const id of nodeIds) adj.set(id, new Set());
    let subjectEdgeCount = 0;
    for (const el of elements) {
        const d = el?.data;
        if (!d?.source || !d?.target) continue;
        if (!nodeIds.has(d.source) || !nodeIds.has(d.target)) continue;
        adj.get(d.source)!.add(d.target);
        adj.get(d.target)!.add(d.source);
        if (d.source === subjectId || d.target === subjectId) subjectEdgeCount++;
    }

    // "Islanded" means the subject is disconnected from OTHER nodes on the
    // canvas. With <= 1 node on the canvas there are no other nodes to be
    // islanded from — the user just dropped a fresh wallet in and triage
    // hasn't filled out neighbours yet. Surfacing the amber "Subject
    // islanded" banner in that state is the canonical false positive.
    const islanded = subjectEdgeCount === 0 && nodeIds.size > 1;

    // Connected-component count (BFS). Subject's own component is excluded
    // from the "orphan" count.
    const seen = new Set<string>();
    let componentCount = 0;
    let subjectComponentSize = 0;
    for (const start of nodeIds) {
        if (seen.has(start)) continue;
        componentCount++;
        // BFS from `start`
        const stack = [start];
        let size = 0;
        let containsSubject = false;
        while (stack.length) {
            const n = stack.pop()!;
            if (seen.has(n)) continue;
            seen.add(n);
            size++;
            if (n === subjectId) containsSubject = true;
            for (const nb of adj.get(n) || []) {
                if (!seen.has(nb)) stack.push(nb);
            }
        }
        if (containsSubject) subjectComponentSize = size;
    }

    // Orphan components = all components EXCEPT the subject's own.
    void subjectComponentSize;
    const orphanComponentCount = Math.max(0, componentCount - 1);

    return { subjectId, islanded, totalNodes: nodeIds.size, orphanComponentCount };
}
